/**
 * PhoneOtpVerify — 전화번호 OTP 발송·확인 공용 컴포넌트
 *
 * PhoneOtpSendButton — 전화번호 입력칸과 같은 줄(오른쪽)에 배치하는 발송 버튼.
 * PhoneOtpVerify     — 입력칸 아래에 배치. 발송 전(link 변형 한정 경고문) → 코드 입력 줄 → 인증 완료 메시지.
 *
 * variant='link'   — SNS 가입: 이미 가입된 번호를 기존 계정에 연동할 때 (경고 톤)
 * variant='verify' — 일반 가입: 본인 확인용 필수 인증 (중립 톤)
 */
import { useId, useState } from 'react';
import { Button } from '@indipia/ui-core';

interface SendButtonProps {
  onClick: () => void;
  loading: boolean;
  disabled?: boolean;
  /** SNS 중복 연동 흐름은 문구가 다르다 */
  label?: string;
}

/** 전화번호 입력칸과 같은 줄(오른쪽)에 배치하는 발송 버튼 */
export function PhoneOtpSendButton({ onClick, loading, disabled, label = '인증번호 발송' }: SendButtonProps) {
  return (
    <Button
      type="button"
      variant="primary"
      size="lg"
      className="shrink-0 whitespace-nowrap"
      loading={loading}
      disabled={disabled}
      onClick={onClick}
    >
      {loading ? '발송 중…' : label}
    </Button>
  );
}

interface Props {
  otpSent: boolean;
  otpVerified: boolean;
  onVerify: (code: string) => void;
  loading: boolean;
  error: string | null;
  variant?: 'link' | 'verify';
}

/** 입력칸 아래에 배치 — 발송 버튼은 PhoneOtpSendButton으로 분리되어 여기 없음 */
export function PhoneOtpVerify({ otpSent, otpVerified, onVerify, loading, error, variant = 'verify' }: Props) {
  const [code, setCode] = useState('');
  const codeId = useId();

  if (otpVerified) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span className="text-caption font-semibold" style={{ color: 'var(--color-success)' }}>
          {variant === 'link' ? '인증 완료 — 기존 계정에 연동됩니다' : '전화번호 인증이 완료됐어요'}
        </span>
      </div>
    );
  }

  const isLink = variant === 'link';

  if (!otpSent) {
    // 발송 전: link 변형만 경고 문구를 보여준다 (발송 버튼은 입력칸 옆에 있음)
    if (!isLink) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 6 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p className="text-caption font-semibold" style={{ color: 'var(--color-warningStrong)' }}>
          이미 가입된 번호예요. OTP 인증 후 기존 계정에 SNS를 연동합니다.
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      {isLink && (
        <p className="text-caption font-semibold" style={{ color: 'var(--color-warningStrong)', marginBottom: 8 }}>
          이미 가입된 번호예요. OTP 인증 후 기존 계정에 SNS를 연동합니다.
        </p>
      )}
      {/*
        입력칸 오른쪽에 확인 버튼이 붙는 조합은 ui-core `Input` 계약으로 표현할 수 없다
        (`Input` 은 `.field > label / input / .help` 를 한 덩어리로 렌더해 입력칸만 감싸는 지점이 없다).
        `.field` 표준 클래스로 직접 조립한다 — 색·높이·포커스 링은 그대로 토큰이 구동한다(D-09 §15.2).
        `Input` 계약에 어펜드 슬롯이 생기면 회수 대상. — PasswordField.tsx 와 동일 사유.
      */}
      <div className={`field field-fill ${error ? 'error' : ''}`}>
        <div className="flex gap-sm">
          <input
            id={codeId}
            aria-label="인증번호"
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="인증번호 6자리"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="input input-lg"
          />
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="shrink-0 whitespace-nowrap"
            loading={loading}
            disabled={code.length < 6}
            onClick={() => onVerify(code)}
          >
            확인
          </Button>
        </div>
        {error && <span className="help">{error}</span>}
      </div>
    </div>
  );
}
